import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft, Plus, Trash2, Loader2, Pencil, Copy, Save, X,
  Image as ImageIcon, FileText, Film, LayoutGrid, Layers,
  Clock, Type, Sparkles, Home, Trophy, Mail, Upload, Figma as FigmaIcon
} from 'lucide-react';

/* ── Category definitions ── */
const CONTENT_CATEGORIES = [
  { value: 'new_listing', label: 'New Listings' },
  { value: 'open_house', label: 'Open House' },
  { value: 'price_adjustment', label: 'Price Adjustment' },
  { value: 'under_contract', label: 'Under Contract' },
  { value: 'sold', label: 'Sold' },
  { value: 'market_report', label: 'Market Report' },
  { value: 'festive', label: 'Festive Post' },
  { value: 'misc', label: 'Misc' },
];

const PRINT_CATEGORIES = [
  { value: 'brochure', label: 'Brochure' },
  { value: 'flyer', label: 'Flyer' },
  { value: 'postcard', label: 'Postcards' },
];

const TABS = [
  { id: 'post', label: 'Post', icon: FileText },
  { id: 'story', label: 'Stories', icon: ImageIcon },
  { id: 'print', label: 'Prints', icon: FileText },
  { id: 'reel', label: 'Reel', icon: Film },
  { id: 'carousel', label: 'Carousel', icon: LayoutGrid },
  { id: 'newsletter', label: 'Newsletter', icon: Mail },
];

const REEL_ICONS = [
  { id: 'sparkles', Icon: Sparkles },
  { id: 'home', Icon: Home },
  { id: 'trophy', Icon: Trophy },
  { id: 'film', Icon: Film },
];
const CAROUSEL_ICONS = [
  { id: 'sparkles', Icon: Sparkles },
  { id: 'home', Icon: Home },
  { id: 'trophy', Icon: Trophy },
  { id: 'grid', Icon: LayoutGrid },
];
const TEMPLATE_COLORS = ['#f59e0b', '#8B5CF6', '#EC4899', '#10b981', '#3b82f6', '#ef4444'];

function getCategoriesForTab(tabId) {
  if (tabId === 'print') return PRINT_CATEGORIES;
  return CONTENT_CATEGORIES;
}

/* ══════════════════════════════════════════════════════════
   Generic Template Tab (Post / Stories / Prints / Newsletter)
   Uses /api/templates with type filter
   ══════════════════════════════════════════════════════════ */
function GenericTemplateTab({ tabType }) {
  const navigate = useNavigate();
  const categories = getCategoriesForTab(tabType);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');

  // For prints, we query multiple types (flyer, postcard, brochure)
  const isPrint = tabType === 'print';
  const PRINT_TYPES = ['flyer', 'postcard', 'brochure'];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (isPrint) {
        // Fetch all print-related types
        const allPrintTemplates = [];
        for (const pt of PRINT_TYPES) {
          const res = await apiClient.get(`/templates?type=${pt}`);
          allPrintTemplates.push(...(res.data.templates || []));
        }
        // Filter by active category (which maps to type for prints)
        if (activeCategory !== 'all') {
          setTemplates(allPrintTemplates.filter(t => t.type === activeCategory));
        } else {
          setTemplates(allPrintTemplates);
        }
      } else {
        const params = new URLSearchParams({ type: tabType });
        if (activeCategory !== 'all') params.set('category', activeCategory);
        const res = await apiClient.get(`/templates?${params}`);
        setTemplates(res.data.templates || []);
      }
    } catch { toast.error('Failed to load templates'); }
    setLoading(false);
  }, [tabType, activeCategory, isPrint]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (tid) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await apiClient.delete(`/templates/${tid}`);
      toast.success('Deleted');
      load();
    } catch { toast.error('Failed to delete'); }
  };

  const handleDuplicate = async (tpl) => {
    try {
      await apiClient.post('/templates', {
        name: `${tpl.name} (Copy)`,
        category: tpl.category,
        type: tpl.type,
        thumbnail_url: tpl.thumbnail_url,
        canvas_data: tpl.canvas_data || { width: 1080, height: 1080, elements: [] },
      });
      toast.success('Duplicated');
      load();
    } catch { toast.error('Failed to duplicate'); }
  };

  const openCanvasDesigner = (templateId) => {
    // For prints, default to 'flyer' type; for others, use the tabType directly
    const canvasType = isPrint ? 'flyer' : tabType;
    if (templateId) {
      navigate(`/canvas-designer/${templateId}?returnTo=/design-studio`);
    } else {
      navigate(`/canvas-designer?type=${canvasType}&returnTo=/design-studio`);
    }
  };

  const [importing, setImporting] = useState(false);

  const handlePdfImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please select a PDF file');
      return;
    }

    setImporting(true);
    try {
      // For prints: use activeCategory if it's a valid print type, otherwise default to 'flyer'
      const PRINT_TYPE_MAP = ['flyer', 'postcard', 'brochure'];
      let canvasType = tabType;
      if (isPrint) {
        canvasType = PRINT_TYPE_MAP.includes(activeCategory) ? activeCategory : 'flyer';
      }
      const formData = new FormData();
      formData.append('file', file);
      formData.append('template_type', canvasType);
      formData.append('page_index', '0');
      // For print types, parse all pages for multi-page templates
      if (isPrint) {
        formData.append('all_pages', 'true');
      }

      const resp = await apiClient.post('/templates/import-pdf', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (resp.data?.success) {
        const stats = resp.data.stats || {};
        toast.success(`Imported: ${stats.text_count || 0} text, ${stats.image_count || 0} images, ${stats.shape_count || 0} shapes`);

        // Multi-page response
        if (resp.data.multi_page && resp.data.pages) {
          sessionStorage.setItem('pdf_import_data', JSON.stringify({
            pages: resp.data.pages,
            target_type: resp.data.target_type,
          }));
        } else {
          // Single page response
          sessionStorage.setItem('pdf_import_data', JSON.stringify({
            elements: resp.data.elements,
            background_image: resp.data.background_image,
            target_type: resp.data.target_type,
          }));
        }
        navigate(`/canvas-designer?type=${canvasType}&import=pdf&returnTo=/design-studio`);
      } else {
        toast.error('Failed to parse PDF');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to import PDF');
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  // ── Figma import ──
  const [figmaOpen, setFigmaOpen] = useState(false);
  const [figmaUrl, setFigmaUrl] = useState('');
  const [figmaToken, setFigmaToken] = useState('');
  const [saveToken, setSaveToken] = useState(true);
  const [hasSavedToken, setHasSavedToken] = useState(false);
  const [flattenBg, setFlattenBg] = useState(false);
  const [figmaImporting, setFigmaImporting] = useState(false);

  const openFigmaDialog = async () => {
    setFigmaOpen(true);
    try {
      const r = await apiClient.get('/templates/figma-token-status');
      setHasSavedToken(!!r.data?.has_saved_token);
    } catch { /* ignore */ }
  };

  const handleFigmaImport = async () => {
    if (!figmaUrl.trim()) { toast.error('Paste a Figma frame link'); return; }
    if (!figmaToken.trim() && !hasSavedToken) { toast.error('Enter your Figma access token'); return; }
    const PRINT_TYPE_MAP = ['flyer', 'postcard', 'brochure'];
    let canvasType = tabType;
    if (isPrint) canvasType = PRINT_TYPE_MAP.includes(activeCategory) ? activeCategory : 'flyer';
    setFigmaImporting(true);
    try {
      const resp = await apiClient.post('/templates/import-figma', {
        figma_url: figmaUrl.trim(),
        token: figmaToken.trim() || undefined,
        template_type: canvasType,
        save_token: saveToken && !!figmaToken.trim(),
        flatten_background: flattenBg,
      });
      if (resp.data?.success) {
        const stats = resp.data.stats || {};
        toast.success(`Imported "${resp.data.frame_name}": ${stats.text_count || 0} text, ${stats.shape_count || 0} shapes, ${stats.image_count || 0} images`);
        sessionStorage.setItem('pdf_import_data', JSON.stringify({
          elements: resp.data.elements,
          background_image: resp.data.background_image,
          background_color: resp.data.background_color,
          target_type: resp.data.target_type,
        }));
        setFigmaOpen(false);
        navigate(`/canvas-designer?type=${canvasType}&import=pdf&returnTo=/design-studio`);
      } else {
        toast.error('Failed to import from Figma');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to import from Figma');
    } finally {
      setFigmaImporting(false);
    }
  };


  return (
    <div data-testid={`tab-${tabType}-content`}>
      {/* Category filter */}
      <div className="flex flex-wrap gap-2 mb-5">
        <button
          onClick={() => setActiveCategory('all')}
          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${activeCategory === 'all' ? 'bg-primary text-primary-foreground border-primary' : 'bg-card border-border text-muted-foreground hover:text-foreground'}`}
          data-testid="category-filter-all"
        >All</button>
        {categories.map(c => (
          <button
            key={c.value}
            onClick={() => setActiveCategory(c.value)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${activeCategory === c.value ? 'bg-primary text-primary-foreground border-primary' : 'bg-card border-border text-muted-foreground hover:text-foreground'}`}
            data-testid={`category-filter-${c.value}`}
          >{c.label}</button>
        ))}
      </div>

      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-muted-foreground">{templates.length} templates</p>
        <div className="flex items-center gap-2">
          <input type="file" accept=".pdf" id={`pdf-import-${tabType}`} className="hidden" onChange={handlePdfImport} />
          <Button
            size="sm"
            variant="outline"
            onClick={() => document.getElementById(`pdf-import-${tabType}`).click()}
            disabled={importing}
            data-testid={`import-pdf-${tabType}-btn`}
          >
            {importing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}
            {importing ? 'Parsing...' : 'Import PDF'}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={openFigmaDialog}
            data-testid={`import-figma-${tabType}-btn`}
          >
            <FigmaIcon className="h-4 w-4 mr-1" /> Import from Figma
          </Button>
          <Button size="sm" onClick={() => openCanvasDesigner()} data-testid={`new-${tabType}-template-btn`}>
            <Plus className="h-4 w-4 mr-1" /> New Template
          </Button>
        </div>
      </div>

      {/* Figma import dialog */}
      <Dialog open={figmaOpen} onOpenChange={setFigmaOpen}>
        <DialogContent className="sm:max-w-md" data-testid="figma-import-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FigmaIcon className="h-4 w-4" /> Import from Figma
            </DialogTitle>
            <DialogDescription>
              Paste a Figma frame link. We render it as a pixel-perfect background and pull text into editable layers.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="figma-url">Figma frame link</Label>
              <Input
                id="figma-url"
                placeholder="https://www.figma.com/design/…?node-id=1-2"
                value={figmaUrl}
                onChange={(e) => setFigmaUrl(e.target.value)}
                data-testid="figma-url-input"
              />
              <p className="text-xs text-muted-foreground">Tip: open the specific frame in Figma and copy its link (it includes <code>node-id</code>).</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="figma-token">
                Figma access token {hasSavedToken && <span className="text-emerald-600">(saved — leave blank to reuse)</span>}
              </Label>
              <Input
                id="figma-token"
                type="password"
                placeholder={hasSavedToken ? '•••••••• using saved token' : 'figd_…'}
                value={figmaToken}
                onChange={(e) => setFigmaToken(e.target.value)}
                data-testid="figma-token-input"
              />
              <p className="text-xs text-muted-foreground">Figma → Settings → Security → Personal access tokens (file read access).</p>
            </div>
            {!!figmaToken.trim() && (
              <div className="flex items-center gap-2">
                <Checkbox id="figma-save" checked={saveToken} onCheckedChange={setSaveToken} data-testid="figma-save-token-checkbox" />
                <Label htmlFor="figma-save" className="text-sm font-normal">Save this token to my profile</Label>
              </div>
            )}
            <div className="flex items-start gap-2 pt-1 border-t border-border">
              <Checkbox id="figma-flatten" checked={flattenBg} onCheckedChange={setFlattenBg} className="mt-0.5" data-testid="figma-flatten-checkbox" />
              <Label htmlFor="figma-flatten" className="text-sm font-normal leading-snug">
                Keep flattened background
                <span className="block text-xs text-muted-foreground">On: locks a pixel-perfect render behind editable text (best for gradients/effects). Off: rebuilds shapes, images &amp; text as separate editable layers.</span>
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFigmaOpen(false)} data-testid="figma-cancel-btn">Cancel</Button>
            <Button onClick={handleFigmaImport} disabled={figmaImporting} data-testid="figma-import-submit-btn">
              {figmaImporting ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Importing…</> : <>Import</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : templates.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <FileText className="h-10 w-10 mx-auto mb-3 opacity-30" />
          <p>No {tabType} templates yet</p>
          <Button size="sm" className="mt-3" onClick={() => openCanvasDesigner()}>Create first template</Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {templates.map(tpl => (
            <div key={tpl.template_id} className="border border-border/50 rounded-xl overflow-hidden bg-card hover:shadow-md transition-shadow"
              data-testid={`tpl-card-${tpl.template_id}`}>
              <div className="h-32 bg-muted/30 flex items-center justify-center">
                {tpl.thumbnail_url ? (
                  <img src={tpl.thumbnail_url} alt={tpl.name} className="w-full h-full object-cover" />
                ) : (
                  <FileText className="h-10 w-10 opacity-20" />
                )}
              </div>
              <div className="p-3">
                <h3 className="font-semibold text-sm truncate">{tpl.name}</h3>
                <p className="text-[11px] text-muted-foreground mt-0.5 capitalize">{(tpl.category || '').replace(/_/g, ' ')}</p>
                <div className="flex gap-1.5 mt-3">
                  <Button variant="outline" size="sm" className="flex-1 text-xs" onClick={() => openCanvasDesigner(tpl.template_id)}
                    data-testid={`edit-tpl-${tpl.template_id}`}>
                    <Pencil className="h-3 w-3 mr-1" /> Edit
                  </Button>
                  <Button variant="outline" size="sm" className="text-xs" onClick={() => handleDuplicate(tpl)}>
                    <Copy className="h-3 w-3" />
                  </Button>
                  <Button variant="outline" size="sm" className="text-xs text-destructive" onClick={() => handleDelete(tpl.template_id)}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


/* ══════════════════════════════════════════════════════════
   Reel Template Tab
   Uses /api/reels/templates
   ══════════════════════════════════════════════════════════ */
function ReelTemplateTab() {
  const navigate = useNavigate();
  const categories = CONTENT_CATEGORIES;
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/reels/templates');
      setTemplates(res.data.templates || []);
    } catch { toast.error('Failed to load reel templates'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = activeCategory === 'all'
    ? templates
    : templates.filter(t => t.category === activeCategory);

  const handleDuplicate = async (tpl) => {
    try {
      await apiClient.post('/reels/templates', {
        name: `${tpl.name} (Copy)`,
        description: tpl.description,
        category: tpl.category,
        color: tpl.color,
        icon: tpl.icon,
        tracks: tpl.tracks || tpl.tracks_config || [],
        layers: tpl.layers || tpl.overlays_config || [],
        audio: tpl.audio || tpl.audio_config || {},
      });
      toast.success('Duplicated');
      load();
    } catch { toast.error('Failed to duplicate'); }
  };

  const handleDelete = async (tid) => {
    if (!window.confirm('Delete this reel template?')) return;
    try {
      await apiClient.delete(`/reels/templates/${tid}`);
      toast.success('Deleted');
      load();
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <div data-testid="tab-reel-content">
      {/* Category filter */}
      <div className="flex flex-wrap gap-2 mb-5">
        <button onClick={() => setActiveCategory('all')}
          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${activeCategory === 'all' ? 'bg-primary text-primary-foreground border-primary' : 'bg-card border-border text-muted-foreground hover:text-foreground'}`}
          data-testid="reel-category-all">All</button>
        {categories.map(c => (
          <button key={c.value} onClick={() => setActiveCategory(c.value)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${activeCategory === c.value ? 'bg-primary text-primary-foreground border-primary' : 'bg-card border-border text-muted-foreground hover:text-foreground'}`}
            data-testid={`reel-category-${c.value}`}>{c.label}</button>
        ))}
      </div>

      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-muted-foreground">{filtered.length} templates</p>
        <Button size="sm" onClick={() => navigate('/admin/reel-templates')} data-testid="manage-reel-templates-btn">
          <Plus className="h-4 w-4 mr-1" /> Create / Edit in Reel Editor
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <Film className="h-10 w-10 mx-auto mb-3 opacity-30" />
          <p>No reel templates{activeCategory !== 'all' ? ` in "${categories.find(c => c.value === activeCategory)?.label}"` : ''}</p>
          <Button size="sm" className="mt-3" onClick={() => navigate('/admin/reel-templates')}>Open Reel Template Editor</Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered.map(tpl => {
            const IconComp = REEL_ICONS.find(i => i.id === tpl.icon)?.Icon || Sparkles;
            const tracks = tpl.tracks || tpl.tracks_config || [];
            const layers = tpl.layers || tpl.overlays_config || [];
            const thumbUrl = tpl.thumbnail_url || tracks.find(t => t.source_url)?.source_url;
            return (
              <div key={tpl.template_id} className="border border-border/50 rounded-xl overflow-hidden bg-card hover:shadow-md transition-shadow"
                data-testid={`reel-tpl-card-${tpl.template_id}`}>
                <div className="relative h-28 bg-muted/30">
                  {thumbUrl ? (
                    <img src={thumbUrl} alt={tpl.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: `${tpl.color}15` }}>
                      <IconComp className="h-10 w-10 opacity-30" style={{ color: tpl.color }} />
                    </div>
                  )}
                  <div className="absolute top-0 left-0 right-0 h-1.5" style={{ backgroundColor: tpl.color }} />
                </div>
                <div className="p-3">
                  <h3 className="font-semibold text-sm truncate">{tpl.name}</h3>
                  <div className="flex items-center gap-3 text-[11px] text-muted-foreground mt-1 mb-3">
                    <span className="flex items-center gap-1"><Layers className="h-3 w-3" /> {tracks.length} slides</span>
                    <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {tpl.total_duration}s</span>
                    <span className="flex items-center gap-1"><Type className="h-3 w-3" /> {layers.length} layers</span>
                  </div>
                  <div className="flex gap-1.5">
                    <Button variant="outline" size="sm" className="flex-1 text-xs" onClick={() => navigate('/admin/reel-templates')}>
                      <Pencil className="h-3 w-3 mr-1" /> Edit
                    </Button>
                    <Button variant="outline" size="sm" className="text-xs" onClick={() => handleDuplicate(tpl)}>
                      <Copy className="h-3 w-3" />
                    </Button>
                    <Button variant="outline" size="sm" className="text-xs text-destructive" onClick={() => handleDelete(tpl.template_id)}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   Carousel Template Tab
   Uses /api/carousel/templates
   ══════════════════════════════════════════════════════════ */
function CarouselTemplateTab() {
  const navigate = useNavigate();
  const categories = CONTENT_CATEGORIES;
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Load carousel templates from both sources
      const [mainRes, carouselRes] = await Promise.allSettled([
        apiClient.get('/templates?type=carousel'),
        apiClient.get('/carousel/templates'),
      ]);
      const mainTemplates = mainRes.status === 'fulfilled' ? (mainRes.value.data.templates || []).map(t => ({ ...t, _source: 'canvas' })) : [];
      const oldTemplates = carouselRes.status === 'fulfilled' ? (carouselRes.value.data.templates || []).map(t => ({ ...t, _source: 'legacy' })) : [];
      setTemplates([...mainTemplates, ...oldTemplates]);
    } catch { toast.error('Failed to load carousel templates'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = activeCategory === 'all'
    ? templates
    : templates.filter(t => t.category === activeCategory);

  const handleDuplicate = async (tpl) => {
    try {
      await apiClient.post('/carousel/templates', {
        name: `${tpl.name} (Copy)`,
        description: tpl.description,
        category: tpl.category,
        color: tpl.color,
        icon: tpl.icon,
        aspect_ratio: tpl.aspect_ratio || '1:1',
        slides: tpl.slides || [],
      });
      toast.success('Duplicated');
      load();
    } catch { toast.error('Failed to duplicate'); }
  };

  const handleDelete = async (tid) => {
    if (!window.confirm('Delete this carousel template?')) return;
    const tpl = templates.find(t => t.template_id === tid);
    try {
      if (tpl?._source === 'canvas') {
        await apiClient.delete(`/templates/${tid}`);
      } else {
        await apiClient.delete(`/carousel/templates/${tid}`);
      }
      toast.success('Deleted');
      load();
    } catch { toast.error('Failed to delete'); }
  };

  if (editing) {
    return (
      <CarouselTemplateEditor
        template={editing === 'new' ? null : editing}
        categories={categories}
        onBack={() => { setEditing(null); load(); }}
      />
    );
  }

  return (
    <div data-testid="tab-carousel-content">
      {/* Category filter */}
      <div className="flex flex-wrap gap-2 mb-5">
        <button onClick={() => setActiveCategory('all')}
          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${activeCategory === 'all' ? 'bg-primary text-primary-foreground border-primary' : 'bg-card border-border text-muted-foreground hover:text-foreground'}`}
          data-testid="carousel-category-all">All</button>
        {categories.map(c => (
          <button key={c.value} onClick={() => setActiveCategory(c.value)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${activeCategory === c.value ? 'bg-primary text-primary-foreground border-primary' : 'bg-card border-border text-muted-foreground hover:text-foreground'}`}
            data-testid={`carousel-category-${c.value}`}>{c.label}</button>
        ))}
      </div>

      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-muted-foreground">{filtered.length} templates</p>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => navigate('/canvas-designer?type=carousel&returnTo=/design-studio')} data-testid="new-carousel-template-btn">
            <Plus className="h-4 w-4 mr-1" /> Design New Carousel
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <LayoutGrid className="h-10 w-10 mx-auto mb-3 opacity-30" />
          <p>No carousel templates{activeCategory !== 'all' ? ` in "${categories.find(c => c.value === activeCategory)?.label}"` : ''}</p>
          <Button size="sm" className="mt-3" onClick={() => setEditing('new')}>Create first template</Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered.map(tpl => {
            const IconComp = CAROUSEL_ICONS.find(i => i.id === tpl.icon)?.Icon || Sparkles;
            const slides = tpl.slides || [];
            return (
              <div key={tpl.template_id} className="border border-border/50 rounded-xl overflow-hidden bg-card hover:shadow-md transition-shadow"
                data-testid={`carousel-tpl-card-${tpl.template_id}`}>
                <div className="relative h-28 bg-muted/30">
                  <div className="absolute top-0 left-0 right-0 h-1.5" style={{ backgroundColor: tpl.color }} />
                  <div className="w-full h-full flex items-center justify-center gap-1 px-4">
                    {slides.length > 0 ? slides.slice(0, 5).map((s, i) => (
                      <div key={i} className="flex-1 h-20 rounded-md border border-border/30 flex items-center justify-center text-[9px] text-muted-foreground"
                        style={{ backgroundColor: s.background_color || '#1a1a2e' }}>
                        {s.background_image ? <ImageIcon className="h-4 w-4 opacity-30" /> : `S${i + 1}`}
                      </div>
                    )) : (
                      <IconComp className="h-10 w-10 opacity-30" style={{ color: tpl.color }} />
                    )}
                  </div>
                </div>
                <div className="p-3">
                  <h3 className="font-semibold text-sm truncate">{tpl.name}</h3>
                  <div className="flex items-center gap-3 text-[11px] text-muted-foreground mt-1 mb-3">
                    <span className="flex items-center gap-1"><Layers className="h-3 w-3" /> {slides.length} slides</span>
                    <span className="capitalize">{(tpl.category || '').replace(/_/g, ' ')}</span>
                  </div>
                  <div className="flex gap-1.5">
                    {tpl._source === 'canvas' ? (
                      <Button variant="default" size="sm" className="flex-1 text-xs" onClick={() => navigate(`/canvas-designer/${tpl.template_id}`)} data-testid={`design-carousel-${tpl.template_id}`}>
                        <Pencil className="h-3 w-3 mr-1" /> Edit Design
                      </Button>
                    ) : (
                      <>
                        <Button variant="outline" size="sm" className="flex-1 text-xs" onClick={() => setEditing(tpl)} data-testid={`edit-meta-carousel-${tpl.template_id}`}>
                          <Pencil className="h-3 w-3 mr-1" /> Settings
                        </Button>
                        <Button variant="default" size="sm" className="flex-1 text-xs" onClick={() => navigate('/carousel-editor?id=new', { state: { carouselTemplate: tpl } })} data-testid={`design-carousel-${tpl.template_id}`}>
                          <LayoutGrid className="h-3 w-3 mr-1" /> Design
                        </Button>
                      </>
                    )}
                    <Button variant="outline" size="sm" className="text-xs" onClick={() => handleDuplicate(tpl)}>
                      <Copy className="h-3 w-3" />
                    </Button>
                    <Button variant="outline" size="sm" className="text-xs text-destructive" onClick={() => handleDelete(tpl.template_id)}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* Carousel template inline editor */
function CarouselTemplateEditor({ template, categories, onBack }) {
  const [name, setName] = useState(template?.name || '');
  const [description, setDescription] = useState(template?.description || '');
  const [category, setCategory] = useState(template?.category || 'new_listing');
  const [color, setColor] = useState(template?.color || '#f59e0b');
  const [icon, setIcon] = useState(template?.icon || 'sparkles');
  const [aspectRatio, setAspectRatio] = useState(template?.aspect_ratio || '1:1');
  const [slideCount, setSlideCount] = useState(template?.slides?.length || 5);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      const slides = Array.from({ length: slideCount }, (_, i) => ({
        background_color: '#1a1a2e',
        background_image: null,
        layers: i === 0 ? [
          { id: `tpl_title_${i}`, type: 'text', x: 100, y: 400, width: 880, height: 80, content: '{{property_type}}', fontSize: 56, fontColor: 'white', fontWeight: 'bold', fontFamily: 'Playfair Display', textAlign: 'center', opacity: 1 },
          { id: `tpl_price_${i}`, type: 'text', x: 100, y: 500, width: 880, height: 60, content: '{{price}}', fontSize: 48, fontColor: color, fontWeight: 'bold', fontFamily: 'Montserrat', textAlign: 'center', opacity: 1 },
        ] : i === slideCount - 1 ? [
          { id: `tpl_cta_${i}`, type: 'text', x: 140, y: 450, width: 800, height: 80, content: 'Schedule a Showing', fontSize: 42, fontColor: 'white', fontWeight: 'bold', fontFamily: 'Montserrat', textAlign: 'center', opacity: 1 },
          { id: `tpl_addr_${i}`, type: 'text', x: 140, y: 550, width: 800, height: 50, content: '{{address}}', fontSize: 28, fontColor: '#aaa', fontWeight: 'normal', fontFamily: 'Montserrat', textAlign: 'center', opacity: 1 },
        ] : [],
      }));

      const payload = { name, description, category, color, icon, aspect_ratio: aspectRatio, slides };
      if (template?.template_id) {
        await apiClient.put(`/carousel/templates/${template.template_id}`, payload);
        toast.success('Template updated');
      } else {
        await apiClient.post('/carousel/templates', payload);
        toast.success('Template created');
      }
      onBack();
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  return (
    <div data-testid="carousel-template-editor-inline">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="sm" onClick={onBack}><X className="h-4 w-4 mr-1" /> Cancel</Button>
        <h2 className="text-base font-semibold">{template ? 'Edit' : 'New'} Carousel Template</h2>
        <div className="flex-1" />
        <Button size="sm" onClick={handleSave} disabled={saving} data-testid="save-carousel-tpl-btn">
          {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
          Save
        </Button>
      </div>
      <div className="max-w-lg space-y-4">
        <div className="space-y-1">
          <Label>Name</Label>
          <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g., Property Showcase" data-testid="carousel-tpl-name" />
        </div>
        <div className="space-y-1">
          <Label>Description</Label>
          <Input value={description} onChange={e => setDescription(e.target.value)} placeholder="Brief description..." data-testid="carousel-tpl-desc" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label>Category</Label>
            <select className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={category} onChange={e => setCategory(e.target.value)} data-testid="carousel-tpl-category">
              {categories.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          <div className="space-y-1">
            <Label>Aspect Ratio</Label>
            <select className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={aspectRatio} onChange={e => setAspectRatio(e.target.value)}>
              <option value="1:1">1:1 (Square)</option>
              <option value="4:5">4:5 (Portrait)</option>
              <option value="9:16">9:16 (Story)</option>
            </select>
          </div>
        </div>
        <div className="space-y-1">
          <Label>Color</Label>
          <div className="flex gap-2">
            {TEMPLATE_COLORS.map(c => (
              <button key={c} className={`w-8 h-8 rounded-full border-2 ${color === c ? 'border-white scale-110' : 'border-transparent'}`}
                style={{ backgroundColor: c }} onClick={() => setColor(c)} />
            ))}
          </div>
        </div>
        <div className="space-y-1">
          <Label>Icon</Label>
          <div className="flex gap-2">
            {CAROUSEL_ICONS.map(({ id, Icon }) => (
              <button key={id} className={`w-10 h-10 rounded-lg flex items-center justify-center border ${icon === id ? 'border-primary bg-primary/10' : 'border-border'}`}
                onClick={() => setIcon(id)}>
                <Icon className="h-5 w-5" />
              </button>
            ))}
          </div>
        </div>
        <div className="space-y-1">
          <Label>Number of Slides</Label>
          <Input type="number" value={slideCount} onChange={e => setSlideCount(Math.max(1, Math.min(10, parseInt(e.target.value) || 5)))} min={1} max={10} />
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   Main Design Studio Page
   ══════════════════════════════════════════════════════════ */
export default function DesignStudioPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('post');

  useEffect(() => {
    if (user && user.role !== 'admin' && user.role !== 'owner') navigate('/dashboard');
  }, [user, navigate]);

  return (
    <div className="min-h-screen bg-background" data-testid="design-studio-page">
      {/* Header */}
      <div className="border-b border-border/50 px-6 py-3 flex items-center gap-4 bg-card/50">
        <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} data-testid="design-studio-back-btn">
          <ArrowLeft className="h-4 w-4 mr-1" /> Dashboard
        </Button>
        <div>
          <h1 className="text-lg font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>Design Studio</h1>
          <p className="text-[11px] text-muted-foreground">Create and manage templates for all content types</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-border/50 bg-card/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-1" data-testid="design-studio-tabs">
            {TABS.map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${isActive ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
                  data-testid={`tab-${tab.id}`}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Tab content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        {activeTab === 'post' && <GenericTemplateTab tabType="post" />}
        {activeTab === 'story' && <GenericTemplateTab tabType="story" />}
        {activeTab === 'print' && <GenericTemplateTab tabType="print" />}
        {activeTab === 'reel' && <ReelTemplateTab />}
        {activeTab === 'carousel' && <CarouselTemplateTab />}
        {activeTab === 'newsletter' && <GenericTemplateTab tabType="newsletter" />}
      </div>
    </div>
  );
}
